import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function TimeDropdownButton({
                                               value,                 // "15:45" یا "3:45 pm" (هرچی بدی نمایش میده)
                                               onSelect,              // (timeString24, timeString12) => void
                                               stepMinutes = 15,
                                               placement = "right",   // "right" | "left"
                                               gap = 0,
                                               width = 80,
                                               maxHeight = 300,
                                               zIndex = 999999,
                                               showHeader = true,
                                               label,
                                               format = "24h",        // "12h" | "24h"
                                           }) {
    const anchorRef = useRef(null);
    const panelRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });

    const times = useMemo(() => buildTimes(stepMinutes), [stepMinutes]);
    const displayValue = useMemo(() => {
        if (!value) return "";
        // اگر "HH:mm" بود و format=12h، تبدیل کن
        if (/^\d{2}:\d{2}$/.test(value) && format === "12h") return to12h(value);
        return value;
    }, [value, format]);

    const updatePosition = () => {
        const el = anchorRef.current;
        if (!el) return;

        const r = el.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // کنار دکمه، هم‌سطح بالا (مثل عکس)
        let left =
            placement === "right"
                ? r.right + gap
                : r.left - width - gap;

        let top = r.top;

        // اگر از راست/چپ بیرون زد، برگردان
        if (left + width > vw - 8) left = vw - width - 8;
        if (left < 8) left = 8;

        // اگر از پایین بیرون زد، تا حد ممکن بالا بکش
        const approxH = Math.min(maxHeight, times.length * 38 + (showHeader ? 44 : 0));
        if (top + approxH > vh - 8) top = Math.max(8, vh - approxH - 8);
        if (top < 8) top = 8;

        setPos({ top:top-50, left:left+50 });
    };

    useLayoutEffect(() => {
        if (open) updatePosition();
        // eslint-disable-next-line
    }, [open, placement, width]);

    useEffect(() => {
        if (!open) return;

        const onResize = () => updatePosition();
        const onScroll = () => updatePosition();

        const onOutside = (e) => {
            const a = anchorRef.current;
            const p = panelRef.current;
            if (!a || !p) return;
            if (!a.contains(e.target) && !p.contains(e.target)) setOpen(false);
        };

        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };

        window.addEventListener("resize", onResize);
        window.addEventListener("scroll", onScroll, true);
        document.addEventListener("mousedown", onOutside, true);
        document.addEventListener("keydown", onKey);

        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("scroll", onScroll, true);
            document.removeEventListener("mousedown", onOutside, true);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    return (
        <>
            <span ref={anchorRef} onClick={() => setOpen((v) => !v)}>
                <span className="cursor-pointer text-xs px-2 white-space-nowrap" style={{ opacity: displayValue ? 1 : 0.6 }}>{`${label}${" "+displayValue}`}</span>
            </span>

            {open &&
                createPortal(
                    <div
                        ref={panelRef}
                        className="zofino-time-dropdown-panel"
                        data-zofino-floating-layer="time-dropdown"
                        onPointerDown={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                        style={{
                            position: "fixed",
                            top: pos.top,
                            left: pos.left,
                            width,
                            zIndex,
                            background: "#fff",
                            borderRadius: 4,
                            boxShadow: "0 18px 45px rgba(0,0,0,0.18)",
                            overflow: "hidden",
                        }}>
                        {showHeader && (
                            <div style={{
                                    height: 44,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "0 12px",
                                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                                    fontSize: 14,
                                    fontWeight: 500,
                                }}>
                                <span>{displayValue || (format === "12h" ? "12:00 am" : "00:00")}</span>
                                {/*<span style={{ opacity: 0.55, fontSize: 12 }}>Enter ↵</span>*/}
                            </div>
                        )}

                        <div className="scrollbar-hide"  style={{ maxHeight, overflow: "auto" }}>
                            {times.map(({ t24, t12 }) => {
                                const text = format === "12h" ? t12 : t24;
                                const isActive =
                                    (format === "12h" && displayValue?.toLowerCase() === t12.toLowerCase()) ||
                                    (format === "24h" && displayValue === t24) ||
                                    (/^\d{2}:\d{2}$/.test(value || "") && value === t24 && format === "12h");
                                return (
                                    <button
                                        key={t24}
                                        type="button"
                                        onClick={() => {
                                            onSelect?.(t24, t12);
                                            setOpen(false);
                                        }}
                                        style={{
                                            width: "100%",
                                            textAlign: "center",
                                            padding: "10px 12px",
                                            border: "none",
                                            background: isActive ? "rgba(0,0,0,0.06)" : "transparent",
                                            cursor: "pointer",
                                            fontSize: 13,
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isActive) e.currentTarget.style.background = "rgba(0,0,0,0.04)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = isActive
                                                ? "rgba(0,0,0,0.06)"
                                                : "transparent";
                                        }}>
                                        {text}
                                    </button>
                                );
                            })}
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
}

function buildTimes(stepMinutes) {
    const out = [];
    for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += stepMinutes) {
            const t24 = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
            out.push({ t24, t12: to12h(t24) });
        }
    }
    return out;
}

function to12h(t24) {
    const [hh, mm] = t24.split(":").map((x) => parseInt(x, 10));
    const ampm = hh >= 12 ? "pm" : "am";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
}
